import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './LeadsPage.css';

function LeadsPage({ accountId: propAccountId }) {
  // 优先使用 props 传入的 accountId（从后端获取），如果没有则使用 localStorage（向后兼容）
  const storedAccountId = localStorage.getItem('accountId');
  const storedLinkedinAccountId = localStorage.getItem('linkedin_account_id');
  const accountId = propAccountId || storedAccountId || storedLinkedinAccountId || null;
  
  // 调试日志：检查 accountId 的值和来源
  useEffect(() => {
    console.log('🔍 LeadsPage accountId 诊断:', {
      propAccountId,
      storedAccountId,
      storedLinkedinAccountId,
      finalAccountId: accountId,
      accountIdType: typeof accountId,
      accountIdLength: accountId?.length,
      isEmpty: !accountId || accountId === ''
    });
    
    if (!accountId || accountId === '') {
      console.warn('⚠️ LeadsPage: accountId 为空，无法加载数据');
      console.warn('   请检查：1) 是否已登录 2) localStorage 中是否有 accountId');
    }
  }, [propAccountId, storedAccountId, storedLinkedinAccountId, accountId]);
  
  // 如果从 props 获取到 accountId，同步到 localStorage
  useEffect(() => {
    if (propAccountId && propAccountId !== localStorage.getItem('accountId')) {
      console.log('✅ LeadsPage: 同步 accountId 到 localStorage:', propAccountId);
      localStorage.setItem('accountId', propAccountId);
      localStorage.setItem('linkedin_account_id', propAccountId);
    }
  }, [propAccountId]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, replied: 0, not_interested: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingLead, setEditingLead] = useState(null);
  const [showAddToCampaign, setShowAddToCampaign] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [lists, setLists] = useState([]);
  const [showAddToList, setShowAddToList] = useState(false);
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [selectedList, setSelectedList] = useState(null);
  const [listLeads, setListLeads] = useState([]);

  const loadLeads = useCallback(async () => {
    if (!accountId || accountId === '') {
      console.error('❌ loadLeads: accountId 为空，跳过加载');
      return;
    }
    
    try {
      setLoading(true);
      console.log('📋 loadLeads: 开始加载，accountId:', accountId);
      const params = { account_id: accountId, limit: 1000 };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (searchTerm) params.search = searchTerm;

      console.log('📤 loadLeads: API 请求参数:', params);
      const response = await axios.get(`${API_BASE_URL}/leads`, { params });
      console.log('📥 loadLeads: API 响应:', {
        success: response.data.success,
        leadsCount: response.data.leads?.length || 0,
        responseData: response.data
      });
      
      if (response.data.success) {
        setLeads(response.data.leads || []);
        console.log(`✅ loadLeads: 成功加载 ${response.data.leads?.length || 0} 条 leads`);
      } else {
        console.warn('⚠️ loadLeads: API 返回 success=false:', response.data);
        alert('加载 leads 失败: ' + (response.data.message || '未知错误'));
      }
    } catch (error) {
      console.error('❌ loadLeads 错误:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        accountId
      });
      const errorMessage = error.response?.data?.error || error.message || '未知错误';
      alert('加载 leads 失败: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  }, [accountId, filterStatus, searchTerm]);

  const loadStats = useCallback(async () => {
    if (!accountId || accountId === '') {
      console.error('❌ loadStats: accountId 为空，跳过加载');
      return;
    }
    
    try {
      console.log('📊 loadStats: 开始加载，accountId:', accountId);
      const response = await axios.get(`${API_BASE_URL}/leads/stats/summary`, {
        params: { account_id: accountId }
      });
      if (response.data.success) {
        setStats(response.data.stats || { total: 0, new: 0, contacted: 0, replied: 0, not_interested: 0 });
        console.log('✅ loadStats: 成功加载统计数据');
      } else {
        console.warn('⚠️ loadStats: API 返回 success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ loadStats 错误:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    }
  }, [accountId]);

  const loadCampaigns = useCallback(async () => {
    if (!accountId || accountId === '') {
      console.error('❌ loadCampaigns: accountId 为空，跳过加载');
      return;
    }
    
    try {
      console.log('📋 loadCampaigns: 开始加载，accountId:', accountId);
      const response = await axios.get(`${API_BASE_URL}/campaigns`, {
        params: { account_id: accountId }
      });
      if (response.data.success) {
        const draftCampaigns = (response.data.campaigns || []).filter(c => c.status === 'draft');
        setCampaigns(draftCampaigns);
        console.log(`✅ loadCampaigns: 成功加载 ${draftCampaigns.length} 个 draft campaigns`);
      } else {
        console.warn('⚠️ loadCampaigns: API 返回 success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ loadCampaigns 错误:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    }
  }, [accountId]);

  const loadLists = useCallback(async () => {
    if (!accountId || accountId === '') {
      console.error('❌ loadLists: accountId 为空，跳过加载');
      return;
    }
    
    try {
      console.log('📁 loadLists: 开始加载，accountId:', accountId);
      const response = await axios.get(`${API_BASE_URL}/leads-lists`, {
        params: { account_id: accountId }
      });
      if (response.data.success) {
        setLists(response.data.lists || []);
        console.log(`✅ loadLists: 成功加载 ${response.data.lists?.length || 0} 个列表`);
      } else {
        console.warn('⚠️ loadLists: API 返回 success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ loadLists 错误:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
    }
  }, [accountId]);

  useEffect(() => {
    console.log('🔔 LeadsPage useEffect 触发:', {
      accountId,
      accountIdValid: accountId && accountId !== '',
      filterStatus,
      searchTerm
    });
    
    if (accountId && accountId !== '') {
      console.log('✅ LeadsPage: accountId 有效，开始加载数据');
      loadLeads();
      loadStats();
      loadCampaigns();
      loadLists();
    } else {
      console.warn('⚠️ LeadsPage: accountId 无效，跳过数据加载');
      console.warn('   提示：请确保已登录，或检查 localStorage 中的 accountId');
    }
  }, [accountId, filterStatus, searchTerm, loadLeads, loadStats, loadCampaigns, loadLists]);

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  const handleDeleteLeads = async () => {
    if (selectedLeads.length === 0) return;
    if (!window.confirm(`确定要删除 ${selectedLeads.length} 个 Leads 吗？`)) return;

    try {
      await axios.post(`${API_BASE_URL}/leads/batch-delete`, { ids: selectedLeads });
      setSelectedLeads([]);
      loadLeads();
      loadStats();
    } catch (error) {
      console.error('Delete leads error:', error);
      alert('Failed to delete leads: ' + error.message);
    }
  };

  const handleUpdateLead = async (leadId, updates) => {
    try {
      await axios.put(`${API_BASE_URL}/leads/${leadId}`, updates);
      setEditingLead(null);
      loadLeads();
      loadStats();
    } catch (error) {
      console.error('Update lead error:', error);
      alert('Failed to update lead: ' + error.message);
    }
  };

  const handleAddToCampaign = async () => {
    if (!selectedCampaign || selectedLeads.length === 0) {
      alert('请选择 Campaign 和至少一个 Lead');
      return;
    }

    try {
      const leadIds = selectedLeads;
      await axios.post(`${API_BASE_URL}/campaigns/${selectedCampaign}/targets/from-leads`, {
        lead_ids: leadIds
      });

      // Update lead status to contacted
      for (const leadId of leadIds) {
        await axios.put(`${API_BASE_URL}/leads/${leadId}`, { status: 'contacted' });
      }

      setShowAddToCampaign(false);
      setSelectedLeads([]);
      setSelectedCampaign('');
      loadLeads();
      loadStats();
      alert(`成功添加 ${leadIds.length} 个 Leads 到 Campaign！`);
    } catch (error) {
      console.error('Add to campaign error:', error);
      alert('Failed to add leads to campaign: ' + error.message);
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) {
      alert('请输入列表名称');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/leads-lists`, {
        account_id: accountId,
        name: newListName,
        description: newListDescription
      });

      setShowCreateList(false);
      setNewListName('');
      setNewListDescription('');
      loadLists();
      alert('列表创建成功！');
    } catch (error) {
      console.error('Create list error:', error);
      alert('Failed to create list: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAddToList = async (listId) => {
    if (selectedLeads.length === 0) {
      alert('请至少选择一个 Lead');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/leads-lists/${listId}/leads`, {
        lead_ids: selectedLeads
      });

      if (response.data.success) {
        setShowAddToList(false);
        setSelectedLeads([]);
        loadLists();
        alert(`成功添加 ${response.data.added} 个 Leads 到列表！`);
      }
    } catch (error) {
      console.error('Add to list error:', error);
      alert('Failed to add leads to list: ' + error.message);
    }
  };

  const handleDeleteList = async (listId, e) => {
    e.stopPropagation(); // Prevent triggering list click
    if (!window.confirm('确定要删除这个列表吗？列表中的 Leads 不会被删除。')) return;

    try {
      await axios.delete(`${API_BASE_URL}/leads-lists/${listId}`);
      loadLists();
      if (selectedList && selectedList.id === listId) {
        setSelectedList(null);
        setListLeads([]);
      }
      alert('列表已删除');
    } catch (error) {
      console.error('Delete list error:', error);
      alert('Failed to delete list: ' + error.message);
    }
  };

  const handleViewList = async (list) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/leads-lists/${list.id}`);
      if (response.data.success) {
        setSelectedList(list);
        setListLeads(response.data.list.leads || []);
      }
    } catch (error) {
      console.error('Load list leads error:', error);
      alert('Failed to load list leads: ' + error.message);
    }
  };

  // 如果 accountId 无效，显示错误提示
  if (!accountId || accountId === '') {
    return (
      <div className="leads-page">
        <div className="error-state" style={{ padding: '40px', textAlign: 'center' }}>
          <h2>⚠️ 无法加载数据</h2>
          <p>accountId 无效或未设置。请确保：</p>
          <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '20px' }}>
            <li>已成功登录 LinkedIn 账号</li>
            <li>localStorage 中包含有效的 accountId</li>
          </ul>
          <p style={{ marginTop: '20px', color: '#666' }}>
            调试信息：propAccountId = {propAccountId || 'null'}, 
            localStorage.accountId = {localStorage.getItem('accountId') || 'null'},
            localStorage.linkedin_account_id = {localStorage.getItem('linkedin_account_id') || 'null'}
          </p>
          <button 
            className="btn btn-primary" 
            onClick={() => window.location.reload()}
            style={{ marginTop: '20px' }}
          >
            刷新页面
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="leads-page">
      <div className="leads-header">
        <h1>📋 Leads Management</h1>
        <div className="leads-actions">
          <button className="btn btn-secondary" onClick={() => setShowCreateList(true)}>
            ➕ 创建列表
          </button>
          {selectedLeads.length > 0 && (
            <>
              <button className="btn btn-primary" onClick={() => setShowAddToCampaign(true)}>
                ➕ 添加到 Campaign ({selectedLeads.length})
              </button>
              <button className="btn btn-info" onClick={() => setShowAddToList(true)}>
                📝 添加到列表 ({selectedLeads.length})
              </button>
              <button className="btn btn-danger" onClick={handleDeleteLeads}>
                🗑️ 批量删除 ({selectedLeads.length})
              </button>
            </>
          )}
        </div>
      </div>

      {/* Lists Panel */}
      {lists.length > 0 && (
        <div className="lists-panel">
          <h3>📁 Lists</h3>
          <div className="lists-grid">
            {lists.map(list => (
              <div key={list.id} className="list-card" onClick={() => handleViewList(list)}>
                <div className="list-header">
                  <h4>{list.name}</h4>
                  <button className="btn btn-sm btn-danger" onClick={(e) => handleDeleteList(list.id, e)}>
                    🗑️
                  </button>
                </div>
                {list.description && <p className="list-description">{list.description}</p>}
                <div className="list-stats">
                  <span>{list.lead_count || 0} leads</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Panel */}
      <div className="stats-panel">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Leads</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.new}</div>
          <div className="stat-label">New</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.contacted}</div>
          <div className="stat-label">Contacted</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.replied}</div>
          <div className="stat-label">Replied</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.not_interested}</div>
          <div className="stat-label">Not Interested</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="replied">Replied</option>
            <option value="not_interested">Not Interested</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search by name, headline, company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Leads Table */}
      <div className="leads-table-container">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : leads.length === 0 ? (
          <div className="empty-state">No leads found</div>
        ) : (
          <table className="leads-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Headline</th>
                <th>Company</th>
                <th>Location</th>
                <th>Status</th>
                <th>Tags</th>
                <th>Source</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={() => handleSelectLead(lead.id)}
                    />
                  </td>
                  <td>
                    <a href={lead.profile_url} target="_blank" rel="noopener noreferrer">
                      {lead.name || 'N/A'}
                    </a>
                  </td>
                  <td>{lead.headline || '-'}</td>
                  <td>{lead.company || '-'}</td>
                  <td>{lead.location || '-'}</td>
                  <td>
                    <span className={`status-badge status-${lead.status}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td>{lead.tags || '-'}</td>
                  <td>{lead.source || '-'}</td>
                  <td>{new Date(lead.added_at).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn btn-sm"
                      onClick={() => setEditingLead(lead)}
                    >
                      ✏️ Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Modal */}
      {editingLead && (
        <div className="modal-overlay" onClick={() => setEditingLead(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Lead</h2>
            <EditLeadForm
              lead={editingLead}
              onSave={(updates) => handleUpdateLead(editingLead.id, updates)}
              onCancel={() => setEditingLead(null)}
            />
          </div>
        </div>
      )}

      {/* Add to Campaign Modal */}
      {showAddToCampaign && (
        <div className="modal-overlay" onClick={() => setShowAddToCampaign(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add to Campaign</h2>
            <p>Selected: {selectedLeads.length} leads</p>
            <div className="form-group">
              <label>Campaign:</label>
              <select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
              >
                <option value="">Select Campaign</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleAddToCampaign}>
                Add
              </button>
              <button className="btn" onClick={() => setShowAddToCampaign(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to List Modal */}
      {showAddToList && (
        <div className="modal-overlay" onClick={() => setShowAddToList(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add to List</h2>
            <p>Selected: {selectedLeads.length} leads</p>
            {lists.length === 0 ? (
              <div>
                <p>还没有列表，请先创建一个列表。</p>
                <button className="btn btn-primary" onClick={() => { setShowAddToList(false); setShowCreateList(true); }}>
                  创建列表
                </button>
              </div>
            ) : (
              <div className="lists-selection">
                {lists.map(list => (
                  <div key={list.id} className="list-option" onClick={() => handleAddToList(list.id)}>
                    <div className="list-option-name">{list.name}</div>
                    <div className="list-option-count">{list.lead_count || 0} leads</div>
                  </div>
                ))}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn" onClick={() => setShowAddToList(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View List Leads Modal */}
      {selectedList && (
        <div className="modal-overlay" onClick={() => { setSelectedList(null); setListLeads([]); }}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>📁 {selectedList.name}</h2>
            {selectedList.description && <p className="list-description">{selectedList.description}</p>}
            <p className="list-stats">共 {listLeads.length} 个 leads</p>
            
            {listLeads.length === 0 ? (
              <div className="empty-state">这个列表还没有 leads</div>
            ) : (
              <div className="list-leads-table">
                <table className="leads-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Headline</th>
                      <th>Company</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Added to List</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listLeads.map(lead => (
                      <tr key={lead.id}>
                        <td>
                          <a href={lead.profile_url} target="_blank" rel="noopener noreferrer">
                            {lead.name || 'N/A'}
                          </a>
                        </td>
                        <td>{lead.headline || '-'}</td>
                        <td>{lead.company || '-'}</td>
                        <td>{lead.location || '-'}</td>
                        <td>
                          <span className={`status-badge status-${lead.status}`}>
                            {lead.status}
                          </span>
                        </td>
                        <td>{new Date(lead.added_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="modal-actions">
              <button className="btn" onClick={() => { setSelectedList(null); setListLeads([]); }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create List Modal */}
      {showCreateList && (
        <div className="modal-overlay" onClick={() => setShowCreateList(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New List</h2>
            <div className="form-group">
              <label>List Name:</label>
              <input
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="Enter list name"
              />
            </div>
            <div className="form-group">
              <label>Description (optional):</label>
              <textarea
                value={newListDescription}
                onChange={(e) => setNewListDescription(e.target.value)}
                placeholder="Enter description"
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleCreateList}>
                Create
              </button>
              <button className="btn" onClick={() => { setShowCreateList(false); setNewListName(''); setNewListDescription(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditLeadForm({ lead, onSave, onCancel }) {
  const [name, setName] = useState(lead.name || '');
  const [headline, setHeadline] = useState(lead.headline || '');
  const [tags, setTags] = useState(lead.tags || '');
  const [notes, setNotes] = useState(lead.notes || '');
  const [status, setStatus] = useState(lead.status || 'new');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ name, headline, tags, notes, status });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Name:</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Headline:</label>
        <input type="text" value={headline} onChange={(e) => setHeadline(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Tags (comma-separated):</label>
        <input type="text" value={tags} onChange={(e) => setTags(e.target.value)} />
      </div>
      <div className="form-group">
        <label>Notes:</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
      </div>
      <div className="form-group">
        <label>Status:</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="replied">Replied</option>
          <option value="not_interested">Not Interested</option>
        </select>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Save</button>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default LeadsPage;

